import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { prayerService, PrayerRequest } from '../../../services/prayer-service';
import { Language } from '../../../constants/language';

interface PrayerRequestSectionProps {
  className?: string;
}

const PrayerRequestSection: React.FC<PrayerRequestSectionProps> = ({ className = '' }) => {
  const { t, i18n } = useTranslation();

  // Prayer form state
  const [prayerForm, setPrayerForm] = useState({
    name: '',
    request: ''
  });
  const [isSubmittingPrayer, setIsSubmittingPrayer] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Handle prayer form submission
  const handlePrayerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrorMessage(null);

    // Validate form
    const validationErrors = prayerService.validatePrayerRequest(prayerForm);
    if (validationErrors.length > 0) {
      setErrorMessage(validationErrors[0]);
      return;
    }

    setIsSubmittingPrayer(true);

    try {
      const prayerData: PrayerRequest = {
        ...prayerForm,
        language: i18n.language as Language
      };

      const result = await prayerService.submitPrayerRequest(prayerData);

      if (result.success) {
        // Reset form
        setPrayerForm({ name: '', request: '' });

        // Show success message
        setShowSuccessMessage(true);

        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 5000);
      } else {
        setErrorMessage(result.message);
      }

    } catch (error) {
      console.error('Error submitting prayer request:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmittingPrayer(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPrayerForm(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  return (
    <section
      id="prayer-request"
      className={`bg-white text-black py-20 ${className}`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black text-white rounded-full mb-6">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            {t('pages.home.prayerRequest.title')}
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('pages.home.prayerRequest.subtitle')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-green-800">
                    {t('pages.home.prayerRequest.success.title')}
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    {t('pages.home.prayerRequest.success.message')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-2xl p-8 md:p-12 border border-gray-200 shadow-lg">
            <form onSubmit={handlePrayerSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-3">
                  {t('pages.home.prayerRequest.form.name')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={prayerForm.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmittingPrayer}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black transition-all text-black placeholder-gray-500 disabled:opacity-50"
                  placeholder={t('pages.home.prayerRequest.form.namePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="request" className="block text-sm font-semibold text-gray-900 mb-3">
                  {t('pages.home.prayerRequest.form.request')}
                </label>
                <textarea
                  id="request"
                  name="request"
                  value={prayerForm.request}
                  onChange={handleInputChange}
                  rows={6}
                  required
                  disabled={isSubmittingPrayer}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black transition-all text-black placeholder-gray-500 resize-none disabled:opacity-50"
                  placeholder={t('pages.home.prayerRequest.form.requestPlaceholder')}
                ></textarea>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmittingPrayer || !prayerForm.name.trim() || !prayerForm.request.trim()}
                  className="w-full bg-black text-white hover:bg-gray-800 px-8 py-4 font-bold text-lg transition-all duration-300 rounded-lg flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmittingPrayer ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>{t('pages.home.prayerRequest.form.sending')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span>{t('pages.home.prayerRequest.form.submit')}</span>
                    </>
                  )}
                </button>
              </div>

              <div className="pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t('pages.home.prayerRequest.confidential')}
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrayerRequestSection;
